import { Box, HStack, Separator, Stack, Text } from '@chakra-ui/react';
import {
  CaretRightIcon,
  ClockCounterClockwiseIcon,
  GlobeIcon,
  QuestionIcon,
  SignOutIcon,
  StampIcon,
  UserIcon,
} from '@phosphor-icons/react';
import {
  BOOKING_HISTORY_PATH,
  Button,
  Drawer,
  HOME_PATH,
  PROFILE_PATH,
  Select,
  STAMP_CARD_PATH,
  SUPPORT_PATH,
  useAuth,
  useLayoutUtils,
  useSettingsStore,
  useSnack,
} from '@supsupclub/ui-components';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

type DrawerLinkProps = {
  icon: ReactNode;
  label: string;
  onClick: () => void;
};

const DrawerLink = ({ icon, label, onClick }: DrawerLinkProps) => (
  <Box
    as="button"
    onClick={onClick}
    w="full"
    py={3}
    px={4}
    borderRadius="xl"
    cursor="pointer"
    css={{
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      transition: 'all 0.2s ease',
      '&:hover': {
        background: 'var(--chakra-colors-brand-50)',
      },
      '&:active': {
        transform: 'scale(0.98)',
      },
    }}
  >
    <Box color="brand.500" flexShrink={0}>
      {icon}
    </Box>
    <Text fontWeight="medium" flex={1} textAlign="left">
      {label}
    </Text>
    <Box color="gray.300" flexShrink={0}>
      <CaretRightIcon size={16} />
    </Box>
  </Box>
);

const LANGUAGE_OPTIONS = [
  { value: 'en', label: '🇬🇧  English' },
  { value: 'nl', label: '🇳🇱  Nederlands' },
  { value: 'pt', label: '🇵🇹  Português' },
  { value: 'de', label: '🇩🇪  Deutsch' },
];

export const SettingsDrawer = () => {
  const { t } = useTranslation('common');
  const { mdUp } = useLayoutUtils();
  const navigate = useNavigate();
  const { setToken, isLoggedIn } = useAuth();
  const { successMessage } = useSnack();
  const qc = useQueryClient();
  const { locale, setLocale, showSettingsState, setShowSettingsState } =
    useSettingsStore((store) => store);

  const handleClose = () => {
    setShowSettingsState(false);
  };

  const navigateAndClose = (path: string) => {
    navigate(path);
    handleClose();
  };

  const { mutate, isPending } = useMutation({
    mutationKey: ['SIGNOUT_QUERY'],
    mutationFn: () => {
      setToken(null);
      return Promise.resolve(true);
    },
    onSuccess: () => {
      successMessage(t('snack.signout'));
      qc.invalidateQueries();
      qc.clear();
      navigate(HOME_PATH);
      handleClose();
    },
  });

  const body = (
    <Stack gap={5} css={{ minWidth: { md: 280 } }}>
      {isLoggedIn && (
        <Stack gap={1}>
          <DrawerLink
            icon={<StampIcon size={22} weight="duotone" />}
            label={t('stampcard.yourStampCards')}
            onClick={() => navigateAndClose(STAMP_CARD_PATH)}
          />
          <DrawerLink
            icon={<ClockCounterClockwiseIcon size={22} weight="duotone" />}
            label={t('bookingHistory.title')}
            onClick={() => navigateAndClose(BOOKING_HISTORY_PATH)}
          />
          <DrawerLink
            icon={<UserIcon size={22} weight="duotone" />}
            label={t('menu.profile')}
            onClick={() => navigateAndClose(PROFILE_PATH)}
          />
          <DrawerLink
            icon={<QuestionIcon size={22} weight="duotone" />}
            label={t('menu.support')}
            onClick={() => navigateAndClose(SUPPORT_PATH)}
          />
        </Stack>
      )}

      {isLoggedIn && <Separator borderColor="gray.100" />}

      <Stack gap={2} px={1}>
        <HStack gap={2} color="gray.500">
          <GlobeIcon size={18} weight="duotone" />
          <Text fontSize="sm" fontWeight="medium">
            {t('placeholder.language')}
          </Text>
        </HStack>
        <Select
          name="language-switch"
          value={locale}
          onChange={(ev) => setLocale(ev.target.value)}
          options={LANGUAGE_OPTIONS}
        />
      </Stack>

      {isLoggedIn && (
        <>
          <Separator borderColor="gray.100" />
          <Button
            variant="outline"
            colorPalette="red"
            loading={isPending}
            onClick={() => mutate()}
            css={{
              marginBottom: { base: 4, md: 0 },
              borderRadius: 'xl',
              transition: 'all 0.2s ease',
              '&:hover': {
                background: 'red.50',
              },
            }}
          >
            <SignOutIcon size={20} />
            {t('action.signout')}
          </Button>
        </>
      )}
    </Stack>
  );

  return (
    <Drawer
      open={showSettingsState}
      placement={mdUp ? 'end' : 'bottom'}
      onOpenChange={(details) => setShowSettingsState(details.open)}
      title="Menu"
      body={body}
    />
  );
};
